import { Capacitor } from "@capacitor/core";
/* eslint-disable unicorn/prefer-global-this */
// import User from "../../services/user";
// import { hash } from "../../services/utils";
// import { language } from "@local/theme";
import { FireBaseHelper } from "../../plugins/FireBaseHelperPlugin";
import type { ClusterId } from "../../types/cluster";
import type { RuleName } from "../../types/rule";
import type { RocIdDeviceCategory, RocIdDeviceType, QuickViewCategory, PairingCategoryItem, PairingDevice } from "../../types/roc-table";
import type { RulesTab, HistoryTab, SmartMode, SmartModes, Platform } from "../../types/misc";
import type {
	BaseActionData,
	SendType, SendEventName, ZwaveMode, HbDataLayerData,
} from "../../types/analytics";

const send = (eventName: SendType, params: object | undefined): void => {
	if (Capacitor.isNativePlatform()) {
		// we send native events via Firebase Analytics
		void FireBaseHelper.logEvent({ name: eventName, params });
	} else {
		window.dataLayer?.push({
			event: `gx.${eventName}`,
			event_name: eventName,
			[eventName]: params,
		});
	}
};

const getBaseActionData = () => ({
	contentId: globalThis.location.href.substring(globalThis.location.protocol.length + 2).replaceAll(".", "_").replace("/#/", ".").replaceAll("/", "."), // window.hbDataLayer["app_platform"]
	// Discussed at 11.05.2026 with HB, shouldn't send this, as it is not anonymized anymore
	// customerId: window.hbDataLayer!.customerId!, // User.userInfo.extension_customerId
	// email: window.hbDataLayer!.email!, // (await hash(User.userInfo.email)).toUpperCase()
	pageType: window.hbDataLayer!.pageType!,
} as const satisfies BaseActionData);

export const updateHbDataLayer = (hbDataLayerData: HbDataLayerData): void => {
	for (const key in hbDataLayerData) {
		if (hbDataLayerData.hasOwnProperty(key)) {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			window.hbDataLayer![key] = hbDataLayerData[key];
		}
	}
};

const getBaseCustomParam = () => ({
	page_language: window.hbDataLayer!.language!, // language
	tld: window.hbDataLayer!.tld!, // globalThis.location.hostname.split(".").pop()
	app_platform: window.hbDataLayer!.app_platform!,
} as const);

const sendError = (errorMessage: string): void => {
	const data = {
		...getBaseActionData(),
		errorMessages: errorMessage,
	} as const;

	send("page_view", data);
};

const sendAction = (contentGroup: object, customParameter: object): void => {
	const data = {
		...getBaseActionData(),
		...contentGroup,
		...getBaseCustomParam(),
		...customParameter,
	} as const;

	send("page_view", data);
};

const sendEvent = (eventName: SendEventName, customClickParameter?: object, customEcommerceParameter?: object): void => {
	const data = {
		linkId: eventName,
		...customClickParameter,
		...customEcommerceParameter,
	} as const;

	send("click", data);
};

export const sendFirstLoad = (): void => {
	if (window.dataLayer) {
		const platform = Capacitor.getPlatform() as Platform;
		const params = {
			event: "page_meta",
			login_status: 0, // TODO: set to 1 when user is logged in, or is 0 fine, as we are on initial page load?
			page_content_type: "spa",
			page_country: navigator.language?.split("-")[1]?.toLowerCase() ?? "de",
			page_location: (globalThis.BUILD_MODE === "production") ? "www.smarthomebyhornbach.com" : "www-dev.smarthomebyhornbach.com",
			page_id: 0,
			page_language: navigator.language?.split("-")[0] ?? "de",
			page_name: globalThis.location.hash.split("/")[1] ?? "initial",
			page_title: document.title,
			page_type: "start",
			app_platform: (platform === "web") ? platform : `${platform} app`,
		} as const;

		if (Capacitor.isNativePlatform()) {
			// we send native events via Firebase Analytics
			void FireBaseHelper.logEvent({ name: "page_meta", params });
		} else {
			window.dataLayer.push(params);
		}
	}
};

/**
 * 3.2.2.1 Status
 * @deprecated Old quickview doesn't exist any more
 */
export const acGatewaySituation = (gatewaySituation: SmartMode, deviceGroupsCount: number, deviceGroupNames: Array<QuickViewCategory>, devicesPerGroup: Array<number>): void => {
	const contentGroup = {
		content_group: "status",
	} as const;
	const customParameter = {
		situation: gatewaySituation,
		device_groups_count: `${deviceGroupsCount}`,
		device_group_names: deviceGroupNames.join(";"),
		devices_per_group: devicesPerGroup.join(";"),
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.1.1 Event: Gateway Situation ändern
 */
export const evGatewaySituation = (gatewaySituation: SmartMode): void => {
	const customClickParameter = {
		event_description: gatewaySituation,
	} as const;

	sendEvent("change_gateway_situation", customClickParameter);
};

/**
 * 3.2.2.2 Geräte
 */
export const acDevices = (deviceCategoriesCount: number, deviceCategoryNames: Array<RocIdDeviceCategory>, devicesPerCategory: Array<number>): void => {
	const contentGroup = {
		content_group: "devices",
	} as const;
	const customParameter = {
		device_categories_count: `${deviceCategoriesCount}`,
		device_category_names: deviceCategoryNames.join(";"),
		devices_per_category: devicesPerCategory.join(";"),
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.2.1 Geräte Details
 */
export const acDeviceDetails = (deviceName: RocIdDeviceType): void => {
	const contentGroup = {
		content_group: "devices",
		sub_content_group: "device_details",
	} as const;
	const customParameter = {
		device_name: deviceName,
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.2.2 Geräte Messwerte
 */
export const acDeviceMeasuredValues = (clusterId: ClusterId, deviceName: RocIdDeviceType): void => {
	// NOTE: sync clusterIds with "device-graph"
	const clusterTypeNames = {
		"000C": "dew point",
		"0400": "illuminance",
		"0402": "temperature",
		"0403": "pressure",
		"0405": "humidity",
		"0702": "power_consumption",
		"FD01": "ph",
		"FD02": "oydo_reduction_potential",
		"FD03": "salt_concentration",
		"FD04": "total_dissolved_solids",
		"FF69": "temperature",
		"FF81": "carbon_monoxide",
		"FF84": "dt_lawn_mower",
		"FF94": "carbon_dioxide",
		"FF95": "volatile_organic_compound",
		"FF9F": "pm2_5",
	} as const satisfies Partial<Record<ClusterId, string>>;

	const contentGroup = {
		content_group: "devices",
		content_group_2: "device_details",
		content_group_3: clusterTypeNames[clusterId] ?? clusterId,
	} as const;
	const customParameter = {
		device_name: deviceName,
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.2.3 Event: Aktualisieren
 * TODO: unused
 */
export const evDeviceUpdate = (deviceName: string): void => { // TODO: check type
	const customClickParameter = {
		event_description: deviceName,
	} as const;

	sendEvent("update", customClickParameter);
};

/**
 * 3.2.2.3 Regeln
 */
export const acRules = (rulesCount: number): void => {
	const contentGroup = {
		content_group: "rules",
	} as const;
	const customParameter = {
		rules_count: `${rulesCount}`,
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.3.1 Event: Regeln filtern
 */
export const evRulesTab = (rulesTab: RulesTab): void => {
	const customClickParameter = {
		event_description: rulesTab,
	} as const;

	sendEvent("filter_rules", customClickParameter);
};

/**
 * 3.2.2.3.2 Regel Details (Vorlagen)
 */
export const acRuleDetailsTemplate = (ruleName: RuleName): void => {
	const contentGroup = {
		content_group: "rules",
		content_group_2: "rule_details",
	} as const;
	const customParameter = {
		rule_name: ruleName,
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.3.3 Regel Details (Zeitschaltplan)
 */
export const acRuleDetailsTimeSwitchingDiagram = (deviceName: RocIdDeviceType, ruleName: RuleName): void => {
	const contentGroup = {
		content_group: "rules",
		content_group_2: "rule_details",
	} as const;
	const customParameter = {
		device_name: deviceName,
		rule_name: ruleName,
	} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.3.4 Event: Gateway Situation ändern
 */
export const evGatewaySituationChange = (gatewaySituations: SmartModes): void => {
	const customClickParameter = {
		event_description: gatewaySituations.join(";"),
	} as const;

	sendEvent("select_gateway_situation", customClickParameter);
};

/**
 * 3.2.2.4 Historie
 */
export const acHistory = (): void => {
	const contentGroup = {
		content_group: "history",
	} as const;
	const customParameter = {};

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.4.1 Event: Historie eingrenzen
 */
export const evHistoryTab = (historyTab: HistoryTab): void => {
	const customClickParameter = {
		event_description: historyTab,
	} as const;

	sendEvent("select_history", customClickParameter);
};

/**
 * 3.2.2.4.2 Event: Historie Filtern
 */
export const evHistoryFilter = (historyFilterCount: number): void => {
	const customClickParameter = {
		event_description: `${historyFilterCount} devices_selected`,
	} as const;

	sendEvent("filter_history_by_device", customClickParameter);
};

/**
 * 3.2.2.5.1 Account
 */
export const acAccount = (): void => {
	const contentGroup = {
		content_group: "settings",
		content_group_2: "account",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.5.2 Event: Account löschen
 */
export const evAccountDelete = (): void => {
	sendEvent("delete_account");
};

/**
 * 3.2.2.5.4 Event: Z-Wave aktivieren
 */
export const evZWaveMode = (zwaveMode: ZwaveMode): void => {
	const customClickParameter = {
		event_description: zwaveMode,
	} as const;

	sendEvent("z-wave", customClickParameter);
};

/**
 * 3.2.2.6 Support
 */
export const acSupport = (): void => {
	const contentGroup = {
		content_group: "support",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.7 About
 */
export const acAbout = (): void => {
	const contentGroup = {
		content_group: "about",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.7.1 AGB
 */
export const acAboutUserTerms = (): void => {
	const contentGroup = {
		content_group: "about",
		content_group_2: "user_terms",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.7.2 Impressum
 */
export const acAboutImprint = (): void => {
	const contentGroup = {
		content_group: "about",
		content_group_2: "imprint",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.8.1 Gateway
 */
export const acAddGateway = (): void => {
	const contentGroup = {
		content_group: "add",
		content_group_2: "gateway",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.8.2 Event: Gateway hinzufügen
 */
export const evAddGateway = (): void => {
	const customClickParameter = {
		event_description: "gateway",
	} as const;
	const customEcommerceParameter = {
		gateway_added: "add_gateway",
	} as const;

	sendEvent("add", customClickParameter, customEcommerceParameter);
};

/**
 * 3.2.2.8.3 Gerät
 */
export const acAddDevice = (): void => {
	const contentGroup = {
		content_group: "add",
		content_group_2: "device",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.8.4 Event: Gerät hinzufügen
 */
export const evAddDevice = (deviceCategoryName: NonNullable<PairingCategoryItem["name"]>, deviceName: NonNullable<PairingDevice["name"]>): void => {
	const customClickParameter = {
		event_description: "device",
		device_category_name: deviceCategoryName,
		device_name: deviceName,
	} as const;
	const customEcommerceParameter = {
		device_added: "add_device",
	} as const;

	sendEvent("add", customClickParameter, customEcommerceParameter);
};

/**
 * 3.2.2.8.5 Gruppe
 */
export const acAddGroup = (): void => {
	const contentGroup = {
		content_group: "add",
		content_group_2: "group",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.8.6 Event: Gruppe erstellen
 */
export const evAddGroup = (devicesCount: number): void => {
	const customClickParameter = {
		event_description: "group",
		devices_count: `${devicesCount}`,
	} as const;
	const customEcommerceParameter = {
		group_added: "add_group",
	} as const;

	sendEvent("add", customClickParameter, customEcommerceParameter);
};

/**
 * 3.2.2.8.7 Regel
 */
export const acAddRule = (): void => {
	const contentGroup = {
		content_group: "add",
		content_group_2: "rule",
	} as const;
	const customParameter = {} as const;

	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.8.8 Event: Regel hinzufügen
 */
export const evAddRule = (gatewaySituations: SmartModes, ruleName: RuleName): void => {
	const customClickParameter = {
		event_description: "rule",
		gateway_situations: gatewaySituations.join(";"),
		rule_name: ruleName,
	} as const;
	const customEcommerceParameter = {
		rule_added: "add_rule",
	} as const;

	sendEvent("add", customClickParameter, customEcommerceParameter);
};

export const acExplorePage = (): void => {
	const contentGroup = {
		content_group: "explore",
	} as const;
	const customParameter = {} as const;
	sendAction(contentGroup, customParameter);
};

/**
 * 3.2.2.8.9 Fehlermeldung
 */
export const acError = (errorMessage: string): void => {
	sendError(errorMessage);
};

/**
 * 3.2 Event: Explore Carousel Item View
 */
export const evExploreCarouselView = (text: string, position: number): void => {
	const customClickParameter = {
		event_description: text,
		position: `${position}`,
	} as const;

	sendEvent("karussel_view", customClickParameter);
};

/**
 * 3.2 Event: Explore Carousel Item Click
 */
export const evExploreCarouselClick = (text: string, position: number): void => {
	const customClickParameter = {
		event_description: text,
		position: `${position}`,
	} as const;

	sendEvent("karussel_click", customClickParameter);
};

/**
 * 3.3 Event: Brands interaction
 */
export const evExploreBrandsInteraction = (text: string, interactionType: string, position: number): void => {
	const customClickParameter = {
		brand_name: text,
		interaction_type: interactionType,
		position: `${position}`,
	} as const;

	sendEvent("webtrekk_ignore", customClickParameter);
};

/**
 * 3.4 Event: Explore Click
 */
export const evExploreClick = (text: string): void => {
	const customClickParameter = {
		event_description: text,
	} as const;

	sendEvent("click", customClickParameter);
};

/**
 * 3.4 Event: Explore Country Change
 */
export const evExploreChangeCountry = (countryName: string): void => {
	const customClickParameter = {
		event_description: countryName,
	} as const;

	sendEvent("change_country", customClickParameter);
};
